import { Button } from "../button/Button.js";
import "../button/index.js";
import { createContext, use, useMemo } from "react";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { faCheckCircle, faInfoCircle as faInfoCircle$1, faShieldExclamation, faShieldXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as AlertDialog from "@radix-ui/react-alert-dialog";
import { twMerge } from "tailwind-merge";
import { jsx } from "react/jsx-runtime";
//#region src/alert-dialog/AlertDialog.tsx
const AlertDialogContext = createContext({ type: "info" });
const AlertDialogRoot = ({ type = "info", children, ...rest }) => {
	const ctx = useMemo(() => ({ type }), [type]);
	return /* @__PURE__ */ jsx(AlertDialog.Root, {
		...rest,
		children: /* @__PURE__ */ jsx(AlertDialogContext, {
			value: ctx,
			children
		})
	});
};
const AlertDialogContent = ({ className, ...rest }) => {
	return /* @__PURE__ */ jsx(AlertDialog.Content, {
		className: twMerge("fixed top-[50%] left-[50%] z-pentOutermost flex max-h-[85vh] w-[90%] max-w-sm translate-x-[-50%] translate-y-[-50%] flex-col gap-y-4 overflow-y-auto rounded-xl bg-white p-6 shadow-lg", "radix-state-open:animate-dialogIn radix-state-closed:animate-dialogOut", className),
		onOpenAutoFocus: (e) => {
			e.preventDefault();
			e.target?.focus();
		},
		...rest
	});
};
const AlertDialogBody = ({ className, ...rest }) => {
	return /* @__PURE__ */ jsx("div", {
		className: twMerge("flex flex-col gap-y-1", className),
		...rest
	});
};
const AlertDialogDescription = ({ className, ...rest }) => {
	return /* @__PURE__ */ jsx(AlertDialog.Description, {
		className: twMerge("text-base text-gray-600", className),
		...rest
	});
};
const AlertDialogTitle = ({ className, ...rest }) => {
	return /* @__PURE__ */ jsx(AlertDialog.Title, {
		className: twMerge("text-xl font-medium", className),
		...rest
	});
};
const AlertDialogActionFooter = ({ className, ...rest }) => {
	return /* @__PURE__ */ jsx("div", {
		className: twMerge("flex justify-between gap-4 pt-4", className),
		...rest
	});
};
const AlertDialogHeader = ({ className, ...rest }) => {
	return /* @__PURE__ */ jsx("div", {
		className: twMerge("flex items-center justify-between", className),
		...rest
	});
};
const AlertDialogClose = ({ className, "aria-label": ariaLabel, ...rest }) => {
	return /* @__PURE__ */ jsx(AlertDialog.Cancel, {
		asChild: true,
		children: /* @__PURE__ */ jsx(Button, {
			variant: "transparent",
			color: "neutral",
			shape: "square",
			className: twMerge("absolute top-3.5 right-3.5", className),
			"aria-label": ariaLabel ?? "Close",
			...rest,
			children: /* @__PURE__ */ jsx(FontAwesomeIcon, {
				size: "lg",
				icon: faXmark
			})
		})
	});
};
const AlertDialogAction = ({ className, children, ...rest }) => {
	const { type } = use(AlertDialogContext);
	const buttonColor = type === "error" ? "error" : "primary";
	return /* @__PURE__ */ jsx(AlertDialog.Action, {
		asChild: true,
		children: /* @__PURE__ */ jsx(Button, {
			variant: "solid",
			color: buttonColor,
			className: twMerge("flex-1", className),
			...rest,
			children
		})
	});
};
const AlertDialogCancel = ({ className, children, ...rest }) => {
	return /* @__PURE__ */ jsx(AlertDialog.Cancel, {
		asChild: true,
		children: /* @__PURE__ */ jsx(Button, {
			variant: "outline",
			color: "neutral",
			className: twMerge("flex-1", className),
			...rest,
			children
		})
	});
};
const AlertDialogIcon = ({ className, customIcon, ...rest }) => {
	const { type } = use(AlertDialogContext);
	const icon = useMemo(() => {
		switch (type) {
			case "success": return faCheckCircle;
			case "error": return faShieldXmark;
			case "warning": return faShieldExclamation;
			default: return faInfoCircle$1;
		}
	}, [type]);
	const typeClassName = useMemo(() => {
		switch (type) {
			case "success": return "bg-green-100";
			case "error": return "bg-red-100";
			case "warning": return "bg-orange-100";
			default: return "bg-blue-100";
		}
	}, [type]);
	const iconColor = useMemo(() => {
		switch (type) {
			case "success": return "text-green-500";
			case "error": return "text-red-500";
			case "warning": return "text-orange-500";
			default: return "text-blue-500";
		}
	}, [type]);
	return /* @__PURE__ */ jsx("div", {
		className: twMerge("relative flex aspect-square h-10 items-center justify-center rounded-full", className, typeClassName),
		...rest,
		children: /* @__PURE__ */ jsx(FontAwesomeIcon, {
			icon: customIcon ?? icon,
			className: iconColor
		})
	});
};
const AlertDialogOverlay = ({ className, ...rest }) => {
	return /* @__PURE__ */ jsx(AlertDialog.AlertDialogOverlay, {
		asChild: true,
		...rest,
		children: /* @__PURE__ */ jsx("div", { className: twMerge("fixed inset-0 z-pentOutermost bg-black/50", "radix-state-open:animate-fadeIn radix-state-closed:animate-fadeOut", className) })
	});
};
const AlertDialogPortal = AlertDialog.Portal;
const AlertDialogTrigger = AlertDialog.Trigger;
const Root = AlertDialogRoot;
const Portal = AlertDialogPortal;
const Content = AlertDialogContent;
const Body = AlertDialogBody;
const Overlay = AlertDialogOverlay;
const Trigger = AlertDialogTrigger;
const Title = AlertDialogTitle;
const Description = AlertDialogDescription;
const Action = AlertDialogAction;
const Header = AlertDialogHeader;
const Footer = AlertDialogActionFooter;
const Cancel = AlertDialogCancel;
const Close = AlertDialogClose;
const Icon = AlertDialogIcon;
//#endregion
export { Action, AlertDialogAction, AlertDialogActionFooter, AlertDialogBody, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogIcon, AlertDialogOverlay, AlertDialogPortal, AlertDialogRoot, AlertDialogTitle, AlertDialogTrigger, Body, Cancel, Close, Content, Description, Footer, Header, Icon, Overlay, Portal, Root, Title, Trigger };
